import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { IonicModule, NavController } from '@ionic/angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { routes } from 'src/app/constants';
import { PATHS } from 'src/app/cx-home/cx-home.component';
import { KnowledgeBaseArticle, RunQueryResult } from 'src/models';
import { AnalyticsService } from 'src/services/analytics.service';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { KnowledgeBaseArticlesService } from 'src/services/knowledge-base-articles.service';
import { MessageService } from 'src/services/message.service';

import { SlackMarkdown } from '../slack-blocks/slack-markdown';

@Component({
  selector: 'app-kb-search',
  templateUrl: './kb-search.component.html',
  styleUrls: ['./kb-search.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RouterModule, NgxTippyModule],
})
export class KbSearchComponent {
  @Input() customerId: string;
  @Output() didComposeResponse = new EventEmitter<any>();
  @ViewChild('searchInput') searchInput;

  searchText: string;
  inputField: string;
  articleId: string;
  article: Partial<KnowledgeBaseArticle>;
  articleContent: string;
  knowledgeBaseArticles: Partial<KnowledgeBaseArticle>[];
  filteredKnowledgeBaseArticles: Partial<KnowledgeBaseArticle>[];
  customQuery: string;
  aiResponse: string;
  aiLinks: { id: string; url: string; title: string }[];
  isLoading = false;
  isAiResponseTypedOut = false;
  slackMarkdown = new SlackMarkdown();

  constructor(
    public authService: AuthService,
    private knowledgeBaseArticlesService: KnowledgeBaseArticlesService,
    private navController: NavController,
    private msgSrvc: MessageService,
    private apiService: ApiService,
    private analyticsService: AnalyticsService,
    private router: Router,
  ) {
    this.getKnowledgeBaseArticles();
  }

  public async getKnowledgeBaseArticles() {
    try {
      this.isLoading = true;

      const knowledgeBaseArticlesResponse = await this.knowledgeBaseArticlesService.list({
        limit: null,
        order: ['title'],
        descending: false,
        where: [
          {
            field: 'status',
            operator: '==',
            value: 'active',
          },
        ],
      });

      this.knowledgeBaseArticles = knowledgeBaseArticlesResponse.data;
      this.filteredKnowledgeBaseArticles = this.knowledgeBaseArticles;

      this.isLoading = false;
    } catch (err) {
      this.msgSrvc.showError(err);

      this.isLoading = false;
    }
  }

  handleFilters() {
    this.filteredKnowledgeBaseArticles = this.knowledgeBaseArticles;

    const term = this.searchText?.trim().toLowerCase();

    if (term) {
      this.filteredKnowledgeBaseArticles = this.knowledgeBaseArticles.filter((knowledgeBaseArticle) =>
        knowledgeBaseArticle.title.toLowerCase().includes(term),
      );
    }
  }

  getContentSubstring(string?: string) {
    return string?.substring(0, 300);
  }

  public async selectArticle(article: string | Partial<KnowledgeBaseArticle>) {
    this.analyticsService.trackEvent('Knowledge Sidebar', 'Expand Article');

    this.article = typeof article === 'string' ? await this.knowledgeBaseArticlesService.getById(article) : article;
    this.articleId = this.article.id;
    this.articleContent = this.slackMarkdown.markdown.render(this.article.content ?? '');
  }

  selectUrl(url: string) {
    this.knowledgeBaseArticles.forEach((article) => {
      if (article.url === url) {
        this.selectArticle(article);
      }
    });
  }

  deselectArticle() {
    this.articleId = undefined;
    this.article = undefined;
  }

  copyLink() {
    navigator.clipboard.writeText(this.article.url);

    this.analyticsService.trackEvent('Knowledge Sidebar', 'Copy Link');

    this.msgSrvc.show('✓ Copied link to clipboard');
  }

  copyAiResponse() {
    navigator.clipboard.writeText(this.aiResponse);

    this.analyticsService.trackEvent('Knowledge Sidebar', 'Copy AI Response');

    this.msgSrvc.show('✓ Copied to clipboard');
  }

  useAiResponse() {
    this.analyticsService.trackEvent('Knowledge Sidebar', 'Use AI Response');

    this.didComposeResponse.next({
      content: this.aiResponse,
    });
  }

  manageKb() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.KNOWLEDGE_BASE}/`);
  }

  clearAiQuery() {
    this.customQuery = '';
    this.aiResponse = '';
    this.searchText = '';
    this.aiLinks = [];
    this.isAiResponseTypedOut = false;
  }

  public async askAi(query?: string) {
    if (!query || !this.authService.isAiFeatureEnabled('suggestResponses')) {
      return;
    }

    this.analyticsService.trackEvent('Knowledge Sidebar', 'Ask AI');

    this.clearAiQuery();
    this.deselectArticle();

    this.customQuery = query;

    try {
      const result: RunQueryResult = await this.apiService.postPromise('/knowledge-base/query', {
        customerId: this.customerId,
        query: this.customQuery,
      });
      const terms = (result.response || 'Unable to generate an answer').split(' ');

      const delay = 20;
      const duration = delay * terms.length;
      this.typeText(terms, delay);

      setTimeout(() => {
        this.isAiResponseTypedOut = true;
        this.aiLinks = result.articles;
      }, duration);
    } catch (e) {
      this.typeText('Unable to get an answer from the API'.split(' '), 20);
      this.aiLinks = [];
    }
  }

  public focusInput() {
    this.searchInput.setFocus();
  }

  useKbArticleContent(article: Partial<KnowledgeBaseArticle>) {
    this.didComposeResponse.next({
      content: `<br><a href="${article.url}">🔗 ${article.title}</a> `,
    });
  }

  typeText(terms: string[], delay: number) {
    if (terms.length > 0) {
      this.aiResponse += terms.shift() + ' ';
      setTimeout(() => {
        this.typeText(terms, delay);
      }, delay);
    }
  }

  async editArticle(article: Partial<KnowledgeBaseArticle>) {
    if (!article.id) return;

    this.router.navigate([`/${PATHS.KNOWLEDGE_BASE}/articles`, article.id, 'edit']);
  }
}
